import React, { useCallback } from "react";
import { UI_CONFIG } from "../../constants/autostack";

interface TimeSlotButtonProps {
  time: string;
  isSelected: boolean;
  onClick: (time: string) => void;
  variant?: "modal" | "dropdown";
  className?: string;
  disabled?: boolean;
}

export const TimeSlotButton: React.FC<TimeSlotButtonProps> = React.memo(
  ({
    time,
    isSelected,
    onClick,
    variant = "modal",
    className = "",
    disabled = false,
  }) => {
    const handleClick = useCallback(() => {
      if (!disabled) {
        onClick(time);
      }
    }, [time, onClick, disabled]);

    const getButtonStyles = () => {
      const baseStyles =
        "px-3 py-2 text-xs font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2";

      if (disabled) {
        return `${baseStyles} rounded-full cursor-not-allowed opacity-40 bg-gray-100 text-gray-400 border border-gray-200`;
      }

      if (variant === "modal") {
        return `${baseStyles} rounded-full transition-all duration-${
          UI_CONFIG.ANIMATION_DURATION
        } 
          focus:ring-blue-500 ${
            isSelected
              ? `bg-accent-pink text-white border border-pink-500 shadow-sm`
              : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 hover:shadow-sm"
          }`;
      } else {
        return `${baseStyles} rounded-md duration-200 focus:ring-pink-500 ${
          isSelected
            ? "bg-pink-100 text-pink-600 border border-pink-300 shadow-sm"
            : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 hover:shadow-sm"
        }`;
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        className={`${getButtonStyles()} ${className}`}
        aria-pressed={isSelected}
        aria-label={`Select ${time}`}
        disabled={disabled}
        aria-disabled={disabled}
      >
        {time}
      </button>
    );
  }
);

TimeSlotButton.displayName = "TimeSlotButton";
